
import { ReactElement, useMemo } from "react";
import { useHubStatisticsFormatter } from "../../useHubStatisticsFormatter";
import { NumberFormatter } from "client/Hooks";
import {
	Dialog,
	FormattedText,
	NoResults
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { VotersOptionListItem } from "./VotersOptionListItem";
import styles from "./VotersDialog.module.less";
import { CommunityPostPollAnswerViewModel } from "client/Api/Communities/communityPostViewer";
import { getTotalVotes } from "./PollViewer";

export type VotersDialogProps = {
	postId: number;
	options: readonly CommunityPostPollAnswerViewModel[];
	voteCounts: Record<string, number>;
	onClose: () => void;
};

export const VotersDialog = (props: VotersDialogProps): ReactElement => {
	const {
		postId,
		options,
		voteCounts,
		onClose
	} = props;

	const numberFormatter: NumberFormatter = useHubStatisticsFormatter();

	const totalVoteCount = useMemo(
		(): number => getTotalVotes(voteCounts),
		[voteCounts]
	);

	return (
		<Dialog
			trackingId="community-poll-voters-dialog"
			height="fill"
		>
			<Dialog.Header
				title={(
					<FormattedText
						text={localize("_TextWithClarificationFormat")}
						thingsToReplace={[
							{
								what: "{{Text}}",
								filler: localize("_TotalVotes")
							},
							{
								what: "{{Clarification}}",
								filler: numberFormatter(totalVoteCount)
							}
						]}
					/>
				)}
				onClose={onClose}
			/>
			<Dialog.Body>
				{totalVoteCount === 0
					? (
						<NoResults
							trackingId="no-votes-yet"
							icon="bars-sort"
							title={localize("_NoVotesYet")}
							message={localize("_ThereAreNoVotesOnThisPollYet")}
							height="fill"
						/>
					)
					: (
						<ul className={styles.list}>
							{options.map(
								(x): ReactElement => (
									<li key={x.id}>
										<VotersOptionListItem
											key={x.id}
											postId={postId}
											option={x}
											voteCount={voteCounts[x.id] ?? 0}
											numberFormatter={numberFormatter}
										/>
									</li>
								)
							)}
						</ul>
					)
				}
			</Dialog.Body>
			<Dialog.Footer/>
		</Dialog>
	);
};
VotersDialog.displayName = "VotersDialog";